import { itemDef, type Station } from "@eldrun/content";
import type { InventoryActions } from "../../net/inventory-actions.ts";
import type { ItemIcons } from "../../render/item-icons.ts";
import { divider, icon, panel } from "../kit/components.ts";
import { h, mount } from "../kit/dom.ts";
import { CRAFT_CATEGORIES, inCategory, ingredientPage, pickRecipe, recipeSubtitle, type CraftCategory, type IngredientView, type LockedHint, type RecipeView, type StationTab } from "./crafting-model.ts";
import { itemHint, itemType } from "./inventory-model.ts";
import { keyHint } from "./settings.ts";

/** O servidor aceita um pedido de fabricação a cada 0,25 s; o painel trava os botões nesse meio-tempo. */
export const CRAFT_COOLDOWN_MS = 260;
/** Quando a receita escolhida some da lista, o "Criar" espera este tanto (ms) antes de aceitar outro clique. */
const SWAP_GUARD_MS = 400;

interface RecipeRow {
  element: HTMLButtonElement;
  sub: HTMLElement;
}

interface Cell {
  element: HTMLElement;
  qty: HTMLElement;
}

/** Troca os filhos só se a sequência mudou (mover nós no meio de um clique o perderia). */
function syncChildren(parent: HTMLElement, children: readonly HTMLElement[]): void {
  const current = parent.children;
  const same = current.length === children.length && children.every((c, i) => current[i] === c);
  if (!same) parent.replaceChildren(...children);
}

/**
 * Fabricação (F) no kit da F79, como o mockup ui-f: estações em abas no topo (Mãos sempre; Bancada e
 * Bigorna quando estão por perto), categorias à esquerda, a lista de receitas descobertas (e as próximas a
 * descobrir) e, à direita, a receita escolhida: ingredientes 2 × 2 → resultado, "tem / precisa" de cada
 * um, "Criar" (`data-times="1"`) e "×N" (`data-times="max"`). Só envia intenções; o servidor confere
 * receita, estação, ingredientes e espaço.
 *
 * Abas, linhas e o detalhe são mantidos por id e atualizados no lugar: a coleta automática muda as
 * quantidades a qualquer momento, e recriar o botão sob o ponteiro perderia o clique e o foco.
 */
export class CraftingPanel {
  readonly element: HTMLElement;
  private readonly actions: InventoryActions;
  private readonly icons: ItemIcons;
  private readonly tabsBar: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly rowsBox: HTMLElement;
  private readonly lockedBox: HTMLElement;
  private readonly tabButtons = new Map<Station, HTMLButtonElement>();
  private readonly categoryButtons = new Map<CraftCategory, HTMLButtonElement>();
  private category: CraftCategory = "todos";
  private readonly rows = new Map<string, RecipeRow>();
  private tab: Station = "maos";
  private selected: string | undefined;
  private page = 0;
  private busyUntil = 0;
  private busyTimer: ReturnType<typeof setTimeout> | undefined;
  private tabs: StationTab[] = [];
  private lockedKey = "";
  /** A receita mostrada no detalhe (os botões leem a mais recente, nunca a da criação). */
  private view: RecipeView | undefined;

  private readonly detail: HTMLElement;
  private readonly detailIcon: HTMLElement;
  private readonly detailName: HTMLElement;
  private readonly detailType: HTMLElement;
  private readonly detailText: HTMLElement;
  private readonly cells: Cell[];
  private readonly result: Cell;
  private readonly pages: HTMLElement;
  private readonly needs: HTMLElement;
  private readonly one: HTMLButtonElement;
  private readonly max: HTMLButtonElement;
  private needsKey = "";
  private detailKey = "-";

  /** `onClose`: o ✕ do painel; sem ele, só esconde. */
  constructor(parent: HTMLElement, actions: InventoryActions, icons: ItemIcons, onClose?: () => void) {
    this.actions = actions;
    this.icons = icons;
    this.tabsBar = h("div", { class: "fab-estacoes", role: "tablist", "aria-label": "Estações" });
    this.empty = h("p", { class: "hint" }, "Nenhuma receita descoberta aqui ainda. Colete materiais para descobrir.");
    this.rowsBox = h("div", { class: "fab-itens" });
    this.lockedBox = h("div", { class: "fab-itens" });
    // Categorias à esquerda (F62; ui-f): ícone, com o nome na dica; as estações ficam no topo.
    const categories = h(
      "div",
      { class: "cat-coluna", role: "toolbar", "aria-label": "Categorias", "aria-orientation": "vertical" },
      ...CRAFT_CATEGORIES.map((c) => {
        const b = h("button", { type: "button", class: "cat-botao", "data-categoria": c.id, title: c.name, "aria-label": c.name, onclick: () => this.setCategory(c.id) }, icon(`categoria-${c.id}`));
        this.categoryButtons.set(c.id, b);
        return b;
      }),
    );

    // O detalhe (ui-f, direita): cabeçalho, 2 × 2 → resultado, ingredientes e os botões.
    const cell = (cls: string): Cell => {
      const qty = h("span", { class: "fab-qtd" });
      return { element: h("div", { class: cls, "aria-hidden": "true" }, qty), qty };
    };
    this.detailIcon = h("div", { class: "fab-topo-icone", "aria-hidden": "true" });
    this.detailName = h("div", { class: "fab-topo-nome" });
    this.detailType = h("div", { class: "fab-topo-tipo" });
    this.detailText = h("p", { class: "fab-topo-texto" });
    this.cells = Array.from({ length: 4 }, () => cell("fab-cel"));
    this.result = cell("fab-resultado");
    this.pages = h("div", { class: "fab-paginas", role: "group", "aria-label": "Páginas dos ingredientes" });
    this.needs = h("div", { class: "needs", "aria-label": "Ingredientes" });
    this.one = h("button", { type: "button", class: "fab-criar", "data-times": "1" }, "Criar");
    this.max = h("button", { type: "button", class: "fab-max", "data-times": "max" });
    this.one.addEventListener("click", () => this.view && this.craft(this.view.id, 1));
    this.max.addEventListener("click", () => this.view && this.craft(this.view.id, this.view.craftable));
    this.detail = h(
      "div",
      { class: "fab-detalhe" },
      h("div", { class: "fab-topo" }, this.detailIcon, h("div", { class: "fab-topo-titulo" }, this.detailName, this.detailType, this.detailText)),
      h("div", { class: "fab-grade" }, h("div", { class: "fab-ingredientes" }, ...this.cells.map((c) => c.element)), icon("icone-seta", "fab-seta"), this.result.element),
      this.pages,
      divider(),
      this.needs,
      h("div", { class: "fab-botoes" }, this.one, this.max),
    );

    this.element = panel(
      { title: "Fabricação", icon: "icone-fabricacao", onClose: onClose ?? (() => this.toggle(false)) },
      this.tabsBar,
      h("div", { class: "fab-corpo" }, categories, h("div", { class: "fab-lista" }, this.empty, this.rowsBox, this.lockedBox), this.detail),
      keyHint("crafting", "Chegue perto de uma Bancada para mais receitas · {} fecha"),
    );
    this.element.id = "fabricacao";
    this.element.classList.add("fab-painel");
    this.element.hidden = true;
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  toggle(force?: boolean): void {
    this.element.hidden = !(force ?? this.element.hidden);
  }

  private setCategory(category: CraftCategory): void {
    this.category = category;
    this.update(this.tabs);
  }

  private select(id: string): void {
    if (id !== this.selected) this.page = 0;
    this.selected = id;
    this.update(this.tabs);
  }

  update(tabs: StationTab[]): void {
    this.tabs = tabs;
    for (const [id, b] of this.categoryButtons) {
      b.classList.toggle("ativa", id === this.category);
      b.setAttribute("aria-pressed", String(id === this.category));
    }
    if (!tabs.some((t) => t.station === this.tab)) this.tab = "maos";
    syncChildren(this.tabsBar, tabs.map((t) => this.tabButton(t)));
    const tab = tabs.find((t) => t.station === this.tab);
    if (!tab) return;
    const shown = tab.recipes.filter((r) => inCategory(this.category, r.output));
    const picked = pickRecipe(shown, this.selected);
    if (picked !== this.selected) {
      this.page = 0;
      // A receita escolhida sumiu (a estação ficou longe, revisão da F82): o "Criar" espera um pouco, para
      // um clique a caminho não fazer a receita que entrou no lugar.
      if (this.selected !== undefined) this.busyUntil = Math.max(this.busyUntil, performance.now() + SWAP_GUARD_MS);
    }
    this.selected = picked;
    const busy = performance.now() < this.busyUntil;
    this.empty.hidden = shown.length > 0;
    this.empty.textContent = tab.recipes.length > 0 ? "Nenhuma receita desta categoria aqui." : "Nenhuma receita descoberta aqui ainda. Colete materiais para descobrir.";
    syncChildren(this.rowsBox, shown.map((r) => this.row(r)));
    this.renderLocked(tab.locked);
    this.renderDetail(shown.find((r) => r.id === picked), busy);
  }

  private tabButton(t: StationTab): HTMLButtonElement {
    let b = this.tabButtons.get(t.station);
    if (!b) {
      b = h(
        "button",
        {
          type: "button",
          role: "tab",
          class: "k-aba",
          "data-station": t.station,
          onclick: () => {
            this.tab = t.station;
            this.update(this.tabs);
          },
        },
        h("span", { class: "k-rotulo" }, t.name),
      );
      this.tabButtons.set(t.station, b);
    }
    const active = t.station === this.tab;
    b.classList.toggle("ativa", active);
    b.setAttribute("aria-selected", String(active));
    return b;
  }

  /** Linha da lista (ícone, nome, tipo e se dá para fazer, ›); clicar escolhe a receita. */
  private row(r: RecipeView): HTMLElement {
    let row = this.rows.get(r.id);
    if (!row) {
      const art = h("span", { class: "fab-item-icone", "aria-hidden": "true" });
      art.style.backgroundImage = `url(${this.icons.url(r.output)})`;
      const sub = h("span", { class: "fab-item-sub" });
      const element = h(
        "button",
        { type: "button", class: "fab-item", "data-recipe": r.id, onclick: () => this.select(r.id) },
        art,
        h("span", { class: "fab-item-nome" }, r.qty > 1 ? `${r.name} ×${r.qty}` : r.name),
        sub,
        icon("icone-chevron", "fab-item-seta"),
      );
      row = { element, sub };
      this.rows.set(r.id, row);
    }
    const chosen = r.id === this.selected;
    row.element.classList.toggle("ativa", chosen);
    row.element.classList.toggle("missing", r.craftable < 1);
    row.element.setAttribute("aria-current", String(chosen));
    row.sub.textContent = recipeSubtitle(r);
    return row.element;
  }

  /** A receita escolhida: cabeçalho, grade 2 × 2 → resultado, "tem / precisa" e os botões (no lugar). */
  private renderDetail(r: RecipeView | undefined, busy: boolean): void {
    this.view = r;
    // O painel atualiza a cada quadro: só mexe no DOM quando a receita, as contas ou a trava mudam.
    const key = r ? `${r.id}|${r.craftable}|${busy}|${this.page}|${r.inputs.map((i) => i.have).join(",")}` : "";
    if (key === this.detailKey) return;
    this.detailKey = key;
    this.detail.hidden = !r;
    if (!r) {
      this.detail.removeAttribute("data-recipe");
      return;
    }
    this.detail.dataset["recipe"] = r.id;
    const def = itemDef(r.output);
    const url = `url(${this.icons.url(r.output)})`;
    this.detailIcon.style.backgroundImage = url;
    this.detailName.textContent = r.qty > 1 ? `${r.name} ×${r.qty}` : r.name;
    this.detailType.textContent = def ? itemType(def) : "";
    this.detailText.textContent = def ? itemHint(def) : "";
    const grid = ingredientPage(r.inputs, this.page);
    this.page = grid.page;
    grid.cells.forEach((input, i) => this.paintCell(this.cells[i]!, input));
    this.result.element.style.backgroundImage = url;
    this.result.element.classList.toggle("pronto", r.craftable > 0);
    this.result.qty.textContent = r.qty > 1 ? String(r.qty) : "";
    this.renderPages(grid.pages);
    this.renderNeeds(r.inputs);
    this.one.disabled = busy || r.craftable < 1;
    this.max.disabled = busy || r.craftable < 2;
    this.max.textContent = r.craftable > 1 ? `×${r.craftable}` : "Máx";
  }

  private paintCell(cell: Cell, input: IngredientView | undefined): void {
    cell.element.style.backgroundImage = input ? `url(${this.icons.url(input.item)})` : "";
    cell.element.classList.toggle("cheia", Boolean(input));
    cell.element.classList.toggle("lack", Boolean(input && input.have < input.need));
    cell.qty.textContent = input && input.need > 1 ? String(input.need) : "";
    cell.element.title = input ? `${input.name} ${input.have}/${input.need}` : "";
  }

  /** Os ◆ das páginas da grade, só quando há mais de quatro ingredientes. */
  private renderPages(pages: number): void {
    this.pages.hidden = pages < 2;
    if (pages < 2) return;
    if (this.pages.children.length !== pages) {
      mount(
        this.pages,
        ...Array.from({ length: pages }, (_, i) =>
          h("button", {
            type: "button",
            class: "fab-pagina",
            "aria-label": `Página ${i + 1}`,
            onclick: () => {
              this.page = i;
              this.update(this.tabs);
            },
          }),
        ),
      );
    }
    [...this.pages.children].forEach((b, i) => b.setAttribute("aria-pressed", String(i === this.page)));
  }

  /** Linhas "tem / precisa" (ui-f): ícone, nome e a conta; redesenha só quando a receita ou as contas mudam. */
  private renderNeeds(inputs: readonly IngredientView[]): void {
    const key = `${this.view?.id}|` + inputs.map((i) => `${i.item}:${i.have}/${i.need}`).join(",");
    if (key === this.needsKey) return;
    this.needsKey = key;
    mount(
      this.needs,
      ...inputs.map((i) => {
        const art = h("span", { class: "fab-ing-icone", "aria-hidden": "true" });
        art.style.backgroundImage = `url(${this.icons.url(i.item)})`;
        // O espaço entre o nome e a conta fica no texto ("Palha 3/3"); a grade cuida do layout.
        return h("div", { class: `fab-ing ${i.have >= i.need ? "have" : "lack"}` }, art, h("span", { class: "fab-ing-nome" }, i.name), " ", h("span", { class: "fab-ing-conta" }, `${i.have}/${i.need}`));
      }),
    );
  }

  /** As próximas descobertas não têm botões: basta redesenhar quando mudam. */
  private renderLocked(locked: readonly LockedHint[]): void {
    const key = JSON.stringify(locked);
    if (key === this.lockedKey) return;
    this.lockedKey = key;
    mount(
      this.lockedBox,
      locked.length > 0 && h("h3", {}, "Próximas descobertas"),
      ...locked.map((l) =>
        h(
          "div",
          { class: "fab-item locked", "data-recipe": l.id },
          h("span", { class: "fab-item-icone", "aria-hidden": "true" }),
          h("span", { class: "fab-item-nome" }, "???"),
          h("span", { class: "fab-item-sub" }, `falta descobrir: ${l.missing.join(", ")}`),
        ),
      ),
    );
  }

  /** Envia o pedido e trava os botões até o servidor aceitar o próximo. */
  private craft(recipeId: string, times: number): void {
    if (performance.now() < this.busyUntil || times < 1) return;
    this.actions.craft(recipeId, times);
    this.busyUntil = performance.now() + CRAFT_COOLDOWN_MS;
    this.update(this.tabs);
    clearTimeout(this.busyTimer);
    this.busyTimer = setTimeout(() => this.update(this.tabs), CRAFT_COOLDOWN_MS + 10);
  }

  remove(): void {
    clearTimeout(this.busyTimer);
    this.element.remove();
  }
}
